import { createFeatureSelector, createSelector } from '@ngrx/store';
import { MonthEndState } from '@app/month-end-process/state/month-end.state';
import { MonthEndProcessStatus } from '../models/month-end-process-status.enum';
import { MonthEndStatusUtil } from '../models/month-end-status.util';
import { MonthEndTaskState } from '../models/month-end-task-state';

export const getMonthEndFeatureState =
  createFeatureSelector<MonthEndState>('monthEndProcess');

export const selectStepTemplates = createSelector(
  getMonthEndFeatureState,
  (state) => state.stepTemplates,
);

export const selectTemplatesLoaded = createSelector(
  getMonthEndFeatureState,
  (state) => state.templatesLoaded,
);

export const selectTemplatesLoading = createSelector(
  getMonthEndFeatureState,
  (state) => state.templatesLoading,
);

export const selectProcessState = createSelector(
  getMonthEndFeatureState,
  (state) => state.processState,
);

export const selectProcessId = createSelector(
  selectProcessState,
  (processState) => processState?.id ?? null,
);

export const selectTaskStates = createSelector(
  selectProcessState,
  (processState) => processState?.taskStates ?? [],
);

let cachedTaskStateMap: Map<number, MonthEndTaskState> | null = null;
let cachedTaskStatesKey: string | null = null;

export const selectTaskStateMap = createSelector(
  selectTaskStates,
  (taskStates): Map<number, MonthEndTaskState> => {
    const newKey = taskStates
      .map(
        (ts) =>
          `${ts.taskId}:${ts.statusId}:${ts.errorMessage ?? ''}:${ts.value ?? ''}`,
      )
      .join('|');

    if (cachedTaskStatesKey === newKey && cachedTaskStateMap) {
      return cachedTaskStateMap;
    }

    const map = new Map<number, MonthEndTaskState>();
    taskStates.forEach((ts) => map.set(ts.taskId, ts));

    cachedTaskStateMap = map;
    cachedTaskStatesKey = newKey;

    return map;
  },
);

export const selectCurrentGlPeriod = createSelector(
  getMonthEndFeatureState,
  (state) => state.currentGlPeriod,
);

export const selectSelectedGlPeriod = createSelector(
  getMonthEndFeatureState,
  (state) => state.selectedGlPeriod,
);

export const selectIsCurrentGlPeriodSelected = createSelector(
  selectCurrentGlPeriod,
  selectSelectedGlPeriod,
  (currentGlPeriod, selectedGlPeriod) =>
    currentGlPeriod !== null &&
    selectedGlPeriod !== null &&
    currentGlPeriod === selectedGlPeriod,
);

export const selectMonthEndProcessRunning = createSelector(
  getMonthEndFeatureState,
  (state) => state.monthEndProcessRunning,
);

export const selectActiveUsers = createSelector(
  getMonthEndFeatureState,
  (state) => state.activeUsers,
);

export const selectActiveUsersLoading = createSelector(
  getMonthEndFeatureState,
  (state) => state.activeUsersLoading,
);

export const selectToggleUserLockStatusLoading = createSelector(
  getMonthEndFeatureState,
  (state) => state.toggleUserLockStatusLoading,
);

export const selectMonthEndLoading = createSelector(
  getMonthEndFeatureState,
  (state) => state.loading,
);

export const selectStartingProcess = createSelector(
  getMonthEndFeatureState,
  (state) => state.startingProcess,
);

export const selectMonthEndInitializing = createSelector(
  getMonthEndFeatureState,
  (state) => state.initializing,
);

export const selectMonthEndOverallLoading = createSelector(
  getMonthEndFeatureState,
  (state) => state.initializing || state.loading || state.templatesLoading,
);

export const selectMonthEndLoadingState = createSelector(
  getMonthEndFeatureState,
  (state) => ({
    isInitializing: state.initializing,
    isLoading: state.loading,
    isTemplatesLoading: state.templatesLoading,
    isAnyLoading: state.initializing || state.loading || state.templatesLoading,
  }),
);

export const selectProcessingTaskId = createSelector(
  getMonthEndFeatureState,
  (state) => state.processingTaskId,
);

export const selectMonthEndError = createSelector(
  getMonthEndFeatureState,
  (state) => state.error,
);

export const selectAllTasksCompleted = createSelector(
  selectTaskStates,
  (taskStates) => {
    if (!taskStates.length) return false;
    return taskStates.every(
      (taskState) =>
        MonthEndStatusUtil.fromTaskState(taskState) ===
        MonthEndProcessStatus.Completed,
    );
  },
);

export const selectCanStartToday = createSelector(
  getMonthEndFeatureState,
  (state) => state.canStartToday,
);

export const selectEarliestRunDate = createSelector(
  getMonthEndFeatureState,
  (state) => state.earliestRunDate,
);
